import type { Schemas } from "@softmax/observatory-client/types";
import { buildHref } from "@softmax/web-addresses";
import { getActualViewer } from "@/data/viewer";
import type { PlayApi } from "../data/play-api";
import { onePagerCoworldOf } from "./coworlds";
import { parseProgramLabel, programDetailOf } from "./programs/program-detail";
import { recentResultsOf } from "./programs/recent-results";
import { starterSourceOf } from "./programs/starter-source";
import type { ProgramDetail } from "./programs/program-records";
import type { ProgramDetailInput } from "./types";
export async function readProgramDetail({
  signal,
  api,
  input,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly api: PlayApi;
  readonly input: ProgramDetailInput;
}): Promise<ProgramDetail> {
  const { label, id, by, starter, divisionId } = input;
  const starters = onePagerCoworldOf(input.corpus)?.starters ?? {};
  const parsed = parseProgramLabel(label);
  const rowRead = id
    ? api.policyVersionDetail(id)
    : parsed
      ? api
          .get<Schemas["PolicyVersionsResponse"]>({
            path: `/stats/policy-versions?${new URLSearchParams({
              name_exact: parsed.name,
              version: String(parsed.version),
              limit: "1",
            })}`,
          })
          .then((page) => page.entries[0] ?? null)
      : Promise.resolve(null);
  // Each dependent read starts as soon as its own inputs arrive.
  const gamesRead = Promise.all([getActualViewer({ signal }), rowRead]).then(
    ([viewer, row]) =>
      viewer && row
        ? api.myEpisodes({
            policyVersionId: row.id,
            limit: 3,
          })
        : null,
  );
  const leagueRead = api.league(input.leagueId);
  const coworldRead = Promise.all([leagueRead, rowRead]).then(
    ([league, row]) =>
      starter && row && starters[row.name] && league?.game.coworld_id
        ? api.coworld(league.game.coworld_id)
        : null,
  );
  const [row, leaderboard, coworld, games, league] = await Promise.all([
    rowRead,
    divisionId
      ? api.leagueIsPublic(input.leagueId).then((pub) =>
          api.divisionLeaderboard({
            divisionId,
            anonymous: pub,
          }),
        )
      : null,
    coworldRead,
    gamesRead,
    leagueRead,
  ]);
  const resultGame = league?.game.game_address;
  const starterPlayerId = row ? starters[row.name] : undefined;
  return programDetailOf({
    label,
    row,
    by,
    entry:
      leaderboard?.status === "published"
        ? (leaderboard.summary.entries.find((e) => e.policy_label === label) ??
          null)
        : null,
    source:
      starter && coworld && starterPlayerId
        ? starterSourceOf({
            manifest: coworld.manifest,
            coworldId: coworld.id,
            playerId: starterPlayerId,
          })
        : null,
    results:
      games && row
        ? recentResultsOf({
            policyVersionId: row.id,
            entries: games.entries,
            replayHrefOf: (episodeId) =>
              resultGame
                ? buildHref({
                    name: "game.home",
                    game: resultGame,
                    search: new URLSearchParams({ e: episodeId }),
                  })
                : buildHref({ name: "episode", episodeId }),
          })
        : null,
  });
}
